import { useTranslation } from "react-i18next"

import { Button, Modal, useToast, Toast } from "@oysterhr/ui"

import useTransitionEmployment from "~/app/mutations/useTransitionEmployment"

import { EmploymentEvent } from "~/app/utils/constants"
import { CompanyHiresModalTypes, CompanyHiresEngagement } from "./types"
import { useCallback } from "react"

interface CompanyHiresModalProps {
  buttonLabel: string
  engagement: CompanyHiresEngagement | null
  setEngagement: (engagement: CompanyHiresEngagement | null) => void
  setDisplayModal: (display?: CompanyHiresModalTypes) => void
  modalType: CompanyHiresModalTypes
}

const actionAttributes: Record<
  CompanyHiresModalTypes,
  { actionText: string; completedText: string; event: EmploymentEvent }
> = {
  [CompanyHiresModalTypes.Delete]: {
    actionText: "Delete",
    completedText: "deleted",
    event: EmploymentEvent.DELETE,
  },
  [CompanyHiresModalTypes.Cancel]: {
    actionText: "Cancel",
    completedText: "canceled",
    event: EmploymentEvent.CANCEL,
  },
}

const CompanyHiresModal = ({
  buttonLabel,
  engagement,
  setDisplayModal,
  setEngagement,
  modalType,
}: CompanyHiresModalProps) => {
  const [transitionEmployment, { loading }] = useTransitionEmployment({
    awaitRefetchQueries: true,
  })
  const toast = useToast()
  const { t } = useTranslation("company_interface/CompanyHiresPage")

  const onClickHandler = useCallback(() => {
    if (engagement) {
      transitionEmployment({
        variables: {
          employmentId: engagement.employmentId,
          event: actionAttributes[modalType].event,
        },
        onCompleted: () => {
          toast({
            render: (
              <Toast variation="success">
                <Toast.Content>
                  {t("actionCompleted", {
                    name: engagement?.name || "Unknown",
                    action: actionAttributes[modalType].completedText,
                  })}
                </Toast.Content>
              </Toast>
            ),
          })
          setDisplayModal(undefined)
        },
      })
    }
  }, [engagement, modalType, setDisplayModal, toast, t, transitionEmployment])

  return (
    <Modal
      isOpen
      onRequestClose={() => {
        setDisplayModal(undefined)
        setEngagement(null)
      }}
    >
      <Modal.Content>
        <p data-testid="hires-modal-text">
          {engagement &&
            t("actionForEngagement", {
              action: actionAttributes[modalType].actionText,
              name: engagement?.name || "unknown",
            })}
        </p>
      </Modal.Content>
      <Modal.Footer>
        <Button variation="danger" isLoading={loading} onClick={onClickHandler}>
          {buttonLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default CompanyHiresModal
